import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Search, X, Package, Pencil, Trash2, Link, Upload,
  ExternalLink, FileText, Tag, DollarSign, Hash, ChevronDown, ArrowLeft, Settings, Download,
  ImagePlus, RefreshCw, Check,
} from 'lucide-react';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';
import ProductPrintSettings from '../components/ProductPrintSettings';
import ProductCosting from '../components/ProductCosting';

const STATUS_MAP = {
  development: { label: '개발중', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  active:      { label: '판매중', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  discontinued:{ label: '단종',   color: 'bg-gray-700 text-gray-400 border-gray-600' },
};

const TABS = ['기본정보', '원가계산', '출력세팅 & 파일'];

function CategoryModal({ categories, onClose, onRefresh }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async e => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await api.post('/products/categories', { name: name.trim() });
    setName('');
    setSaving(false);
    onRefresh();
  };

  const handleDelete = async id => {
    if (!confirm('카테고리를 삭제하시겠습니까?')) return;
    await api.delete(`/products/categories/${id}`);
    onRefresh();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">카테고리 관리</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800"><X size={16} /></button>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto">
            {categories.length === 0 && <p className="text-xs text-gray-600 text-center py-3">카테고리가 없습니다</p>}
            {categories.map(c => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-800">
                <span className="text-sm text-white">{c.name}</span>
                <button onClick={() => handleDelete(c.id)} className="text-gray-600 hover:text-red-400 transition-colors"><X size={13} /></button>
              </div>
            ))}
          </div>
          <form onSubmit={handleAdd} className="flex gap-2 pt-2 border-t border-gray-800">
            <input className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-brand"
              placeholder="새 카테고리명" value={name} onChange={e => setName(e.target.value)} autoFocus />
            <button type="submit" disabled={saving}
              className="px-3 py-2 text-xs text-white bg-brand rounded-xl hover:bg-brand/80 disabled:opacity-50 flex-shrink-0">
              {saving ? '추가 중...' : '+ 추가'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.development;
  return <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${s.color}`}>{s.label}</span>;
}

function SaleLinkEditor({ links, onChange }) {
  const add = () => onChange([...links, { label: '', url: '' }]);
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));
  const update = (i, field, val) => onChange(links.map((l, idx) => idx === i ? { ...l, [field]: val } : l));

  return (
    <div className="flex flex-col gap-2">
      {links.map((l, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input className="w-28 bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-brand flex-shrink-0"
            placeholder="판매처명" value={l.label} onChange={e => update(i, 'label', e.target.value)} />
          <input className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-brand"
            placeholder="https://..." value={l.url} onChange={e => update(i, 'url', e.target.value)} />
          <button onClick={() => remove(i)} className="p-1 text-gray-500 hover:text-red-400 flex-shrink-0"><X size={13} /></button>
        </div>
      ))}
      <button onClick={add}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand mt-1">
        <Plus size={12} /> 링크 추가
      </button>
    </div>
  );
}

function ProductModal({ product, categories, onClose, onSave }) {
  const [form, setForm] = useState({
    name: product?.name || '',
    partNumber: product?.partNumber || '',
    categoryId: product?.categoryId || '',
    consumerPrice: product?.consumerPrice || '',
    status: product?.status || 'development',
    saleLinks: product?.saleLinks || [],
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave({ ...form, categoryId: form.categoryId || null, consumerPrice: form.consumerPrice || null });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">{product ? '상품 수정' : '상품 추가'}</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">상품명 *</label>
            <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
              placeholder="상품명" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">품번</label>
              <input className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
                placeholder="A-001" value={form.partNumber} onChange={e => setForm(p => ({ ...p, partNumber: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">카테고리</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.categoryId} onChange={e => setForm(p => ({ ...p, categoryId: e.target.value }))}>
                <option value="">없음</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">소비자가 (원)</label>
              <input type="number" step="any" className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-brand"
                placeholder="0" value={form.consumerPrice} onChange={e => setForm(p => ({ ...p, consumerPrice: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">상태</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none"
                value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                <option value="development">개발중</option>
                <option value="active">판매중</option>
                <option value="discontinued">단종</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-2 block">판매처 링크</label>
            <SaleLinkEditor links={form.saleLinks} onChange={links => setForm(p => ({ ...p, saleLinks: links }))} />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs text-gray-400 border border-gray-700 rounded-xl hover:bg-gray-800">취소</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-xs text-white bg-brand rounded-xl hover:bg-brand/80 disabled:opacity-50">
              {saving ? '저장 중...' : product ? '수정' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProductDetail({ product, categories, onEdit, onDelete, onUpdate }) {
  const [activeTab, setActiveTab] = useState(0);
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [renamingIdx, setRenamingIdx] = useState(null);
  const [renameVal, setRenameVal] = useState('');

  const handleFileUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post(`/products/${product.id}/upload-file`, fd);
    onUpdate({ ...product, printFiles: [...(product.printFiles || []), res.data] });
    setUploadingFile(false);
    e.target.value = '';
  };

  const handleFileDelete = async (url) => {
    if (!confirm('파일을 삭제하시겠습니까?')) return;
    await api.delete(`/products/${product.id}/file`, { data: { url } });
    onUpdate({ ...product, printFiles: (product.printFiles || []).filter(f => f.url !== url) });
  };

  const startRename = (idx, name) => { setRenamingIdx(idx); setRenameVal(name); };

  const commitRename = async () => {
    if (renamingIdx === null) return;
    const trimmed = renameVal.trim();
    if (trimmed && trimmed !== (product.printFiles || [])[renamingIdx]?.name) {
      const updated = (product.printFiles || []).map((f, i) => i === renamingIdx ? { ...f, name: trimmed } : f);
      const res = await api.put(`/products/${product.id}`, { printFiles: updated });
      onUpdate(res.data);
    }
    setRenamingIdx(null);
  };

  return (
    <div className="flex flex-col gap-5 pb-6">
      {/* 헤더 */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-white">{product.name}</h2>
            <StatusBadge status={product.status} />
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {product.partNumber && <span className="flex items-center gap-1"><Hash size={11} />{product.partNumber}</span>}
            {product.category && <span className="flex items-center gap-1"><Tag size={11} />{product.category.name}</span>}
            {product.consumerPrice && <span className="flex items-center gap-1"><DollarSign size={11} />{product.consumerPrice.toLocaleString()}원</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onEdit(product)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 border border-gray-700 rounded-xl hover:bg-gray-800 hover:text-white">
            <Pencil size={12} /> 수정
          </button>
          <button onClick={() => onDelete(product)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-400 border border-red-400/30 rounded-xl hover:bg-red-400/10">
            <Trash2 size={12} /> 삭제
          </button>
        </div>
      </div>

      {/* 판매처 링크 */}
      {product.saleLinks && product.saleLinks.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {product.saleLinks.map((l, i) => (
            <a key={i} href={l.url} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-300 bg-gray-800 border border-gray-700 rounded-xl hover:border-gray-500 hover:text-white">
              <Link size={11} /> {l.label || '링크'} <ExternalLink size={10} className="text-gray-500" />
            </a>
          ))}
        </div>
      )}

      {/* 탭 */}
      <div className="border-b border-gray-800">
        <div className="flex gap-1">
          {TABS.map((tab, i) => (
            <button key={i} onClick={() => setActiveTab(i)}
              className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                activeTab === i ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}>
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 0 && (
        <ProductBasicInfo key={product.id} product={product} onUpdate={onUpdate} />
      )}

      {activeTab === 1 && (
        <ProductCosting key={product.id} product={product} onUpdate={onUpdate} />
      )}

      {activeTab === 2 && (
        <div className="flex flex-col gap-4">
          <ProductPrintSettings key={product.id} product={product} onUpdate={onUpdate} />

          {/* 첨부 파일 */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">첨부 파일</span>
              <button onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand">
                <Upload size={12} /> {uploadingFile ? '업로드 중...' : '파일 추가'}
              </button>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
            </div>
            <div className="flex flex-col gap-1.5">
              {(product.printFiles || []).map((f, i) => {
                const proxyKey = f.url.startsWith('/api/s3/proxy')
                  ? new URLSearchParams(f.url.split('?')[1]).get('key')
                  : null;
                const downloadUrl = proxyKey
                  ? `/api/s3/proxy?key=${encodeURIComponent(proxyKey)}&download=${encodeURIComponent(f.name)}`
                  : f.url;
                return (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl">
                    <span className="flex items-center gap-2 text-xs text-gray-300 truncate flex-1 min-w-0">
                      <FileText size={12} className="flex-shrink-0 text-gray-500" />
                      {renamingIdx === i ? (
                        <input
                          autoFocus
                          className="flex-1 bg-transparent text-xs text-white border-b border-brand focus:outline-none"
                          value={renameVal}
                          onChange={e => setRenameVal(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingIdx(null); }}
                          onBlur={commitRename}
                        />
                      ) : (
                        <span className="truncate">{f.name}</span>
                      )}
                    </span>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <a href={downloadUrl} download={f.name}
                        className="p-1 text-gray-500 hover:text-brand" title="다운로드">
                        <Download size={12} />
                      </a>
                      <button onClick={() => startRename(i, f.name)} className="p-1 text-gray-600 hover:text-gray-300" title="파일명 수정">
                        <Pencil size={11} />
                      </button>
                      <button onClick={() => handleFileDelete(f.url)} className="p-1 text-gray-600 hover:text-red-400">
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {(!product.printFiles || product.printFiles.length === 0) && (
                <p className="text-xs text-gray-600 text-center py-4">첨부 파일이 없습니다</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm text-white">{value || <span className="text-gray-600 italic">미입력</span>}</p>
    </div>
  );
}

function TagList({ items, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editVal, setEditVal] = useState('');
  const inputCls = 'bg-transparent text-sm text-white focus:outline-none placeholder-gray-600 flex-1 min-w-0';

  const add = () => {
    const v = input.trim();
    if (!v) return;
    onChange([...items, v]);
    setInput('');
  };

  const startEdit = (i) => {
    setEditingIdx(i);
    setEditVal(items[i]);
  };

  const commitEdit = () => {
    if (editingIdx === null) return;
    const v = editVal.trim();
    if (v) {
      onChange(items.map((it, j) => j === editingIdx ? v : it));
    } else {
      onChange(items.filter((_, j) => j !== editingIdx));
    }
    setEditingIdx(null);
    setEditVal('');
  };

  return (
    <div className="flex flex-col gap-1.5">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2 group">
          <span className="text-gray-600 text-xs">·</span>
          {editingIdx === i ? (
            <input
              autoFocus
              className={inputCls}
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                if (e.key === 'Escape') { setEditingIdx(null); }
              }}
              onBlur={commitEdit}
            />
          ) : (
            <span className="text-sm text-white flex-1 cursor-text" onClick={() => startEdit(i)}>{it}</span>
          )}
          <button onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-600 hover:text-red-400 transition-all">
            <X size={11} />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-gray-700 text-xs">+</span>
        <input
          className={inputCls}
          placeholder={placeholder}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          onBlur={add}
        />
      </div>
    </div>
  );
}

function ProductBasicInfo({ product, onUpdate }) {
  const inputCls = 'w-full bg-gray-800/50 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-brand placeholder-gray-600';
  const labelCls = 'text-xs text-gray-400 mb-1 block';
  const sectionCls = 'bg-gray-900 border border-gray-800 rounded-2xl p-4 flex flex-col gap-3';

  const imageInputRef = useRef(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const saveTimer = useRef(null);
  const saveStatusTimer = useRef(null);

  const [usp, setUsp] = useState(() => ({
    headline: '', features: [], target: '', useCases: [], differentiators: [],
    ...(product.usp || {}),
  }));
  const uspRef = useRef(usp);
  useEffect(() => { uspRef.current = usp; }, [usp]);

  const triggerSave = useCallback((data) => {
    clearTimeout(saveTimer.current);
    clearTimeout(saveStatusTimer.current);
    setSaveStatus('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await api.put(`/products/${product.id}`, { usp: data });
        onUpdate(res.data);
        setSaveStatus('saved');
        saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
      } catch { setSaveStatus('error'); }
    }, 600);
  }, [product.id, onUpdate]);

  const setUspField = useCallback((key, val) => {
    setUsp(prev => {
      const next = { ...prev, [key]: val };
      triggerSave(next);
      return next;
    });
  }, [triggerSave]);

  const handleImageUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgLoading(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await api.post(`/products/${product.id}/upload-image`, fd);
      onUpdate(res.data);
    } catch {}
    setImgLoading(false);
    e.target.value = '';
  };

  const handleFetchImage = async () => {
    const prinmannLink = (product.saleLinks || []).find(l => l.label === '프린만몰' || l.url?.includes('prinmann.com/shop'));
    if (!prinmannLink) return;
    setImgLoading(true);
    try {
      const res = await api.post(`/products/${product.id}/fetch-image`, { url: prinmannLink.url });
      onUpdate(res.data);
    } catch (e) { alert(e.response?.data?.message || '이미지 가져오기 실패'); }
    setImgLoading(false);
  };

  const hasPrinmann = (product.saleLinks || []).some(l => l.label === '프린만몰' || l.url?.includes('prinmann.com/shop'));

  return (
    <div className="flex flex-col gap-4">
      {/* 상품 이미지 + 기본 스펙 */}
      <div className="flex gap-4">
        {/* 이미지 */}
        <div className="flex-shrink-0">
          <div className="w-36 h-36 rounded-2xl border border-gray-800 bg-gray-900 overflow-hidden relative group">
            {product.imageUrl ? (
              <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-700">
                <Package size={32} />
              </div>
            )}
            {imgLoading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-brand border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div className="flex gap-1.5 mt-2">
            <button onClick={() => imageInputRef.current?.click()}
              className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] text-gray-500 hover:text-white bg-gray-800 rounded-lg transition-colors">
              <ImagePlus size={11} /> 업로드
            </button>
            {hasPrinmann && (
              <button onClick={handleFetchImage} disabled={imgLoading}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] text-gray-500 hover:text-brand bg-gray-800 rounded-lg transition-colors disabled:opacity-50">
                <RefreshCw size={11} /> 자동
              </button>
            )}
            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          </div>
        </div>

        {/* 기본 스펙 */}
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-2 gap-3">
            <InfoField label="상품명" value={product.name} />
            <InfoField label="품번" value={product.partNumber} />
            <InfoField label="카테고리" value={product.category?.name} />
            <InfoField label="소비자가" value={product.consumerPrice ? product.consumerPrice.toLocaleString() + '원' : null} />
            <InfoField label="상태" value={STATUS_MAP[product.status]?.label} />
            <InfoField label="등록자" value={product.creator?.name} />
          </div>
        </div>
      </div>

      {/* USP */}
      <div className={sectionCls}>
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">USP / 상품 소개 정보</p>
          <span className="text-[10px] text-gray-600">
            {saveStatus === 'saving' ? '저장 중...' : saveStatus === 'saved' ? '저장됨 ✓' : saveStatus === 'error' ? '저장 실패' : ''}
          </span>
        </div>

        {/* 한 줄 소개 */}
        <div>
          <label className={labelCls}>한 줄 소개</label>
          <input className={inputCls} placeholder="이 상품을 한 문장으로 표현하면?"
            value={usp.headline}
            onChange={e => setUspField('headline', e.target.value)} />
        </div>

        {/* 타겟 고객 */}
        <div>
          <label className={labelCls}>타겟 고객</label>
          <input className={inputCls} placeholder="예: 야구 동호회, 단체 기념품을 찾는 팀..."
            value={usp.target}
            onChange={e => setUspField('target', e.target.value)} />
        </div>

        {/* 주요 특징 */}
        <div>
          <label className={labelCls}>주요 특징 <span className="text-gray-600">(Enter로 추가)</span></label>
          <div className={`${inputCls} py-3`}>
            <TagList items={usp.features} placeholder="특징 입력 후 Enter..."
              onChange={val => setUspField('features', val)} />
          </div>
        </div>

        {/* 추천 사용 상황 */}
        <div>
          <label className={labelCls}>추천 사용 상황 / 용도 <span className="text-gray-600">(Enter로 추가)</span></label>
          <div className={`${inputCls} py-3`}>
            <TagList items={usp.useCases} placeholder="사용 상황 입력 후 Enter..."
              onChange={val => setUspField('useCases', val)} />
          </div>
        </div>

        {/* 차별화 포인트 */}
        <div>
          <label className={labelCls}>차별화 포인트 <span className="text-gray-600">(Enter로 추가)</span></label>
          <div className={`${inputCls} py-3`}>
            <TagList items={usp.differentiators} placeholder="경쟁 상품 대비 강점 입력 후 Enter..."
              onChange={val => setUspField('differentiators', val)} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [catModal, setCatModal] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [pRes, cRes] = await Promise.all([api.get('/products'), api.get('/products/categories')]);
    setProducts(pRes.data);
    setCategories(cRes.data);
    if (pRes.data.length > 0) setSelected(pRes.data[0]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = products.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !(p.partNumber || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCategory && p.categoryId !== Number(filterCategory)) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    return true;
  });

  const handleSave = async form => {
    if (modal?.product) {
      const res = await api.put(`/products/${modal.product.id}`, form);
      setProducts(prev => prev.map(p => p.id === modal.product.id ? res.data : p));
      if (selected?.id === modal.product.id) setSelected(res.data);
    } else {
      const res = await api.post('/products', form);
      setProducts(prev => [...prev, res.data]);
      setSelected(res.data);
    }
    setModal(null);
  };

  const handleDelete = async p => {
    if (!confirm(`'${p.name}'을(를) 삭제하시겠습니까?`)) return;
    await api.delete(`/products/${p.id}`);
    setProducts(prev => prev.filter(x => x.id !== p.id));
    if (selected?.id === p.id) setSelected(null);
  };

  const handleUpdate = useCallback(updated => {
    setProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
    setSelected(updated);
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader icon={Package} title="상품 리스트" count={filtered.length}>
        <button onClick={() => setCatModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400 border border-gray-700 rounded-lg hover:bg-gray-800 hover:text-white">
          <Settings size={14} /> 카테고리
        </button>
        <button onClick={() => setModal({ product: null })}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
          <Plus size={14} /> 상품 추가
        </button>
      </PageHeader>

      {/* 검색 + 필터 */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="상품명 또는 부품번호로 검색..."
            className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand" />
        </div>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="bg-gray-900 border border-gray-800 text-gray-400 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-brand">
          <option value="">전체 카테고리</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="bg-gray-900 border border-gray-800 text-gray-400 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-brand">
          <option value="">전체 상태</option>
          <option value="development">개발중</option>
          <option value="active">판매중</option>
          <option value="discontinued">단종</option>
        </select>
      </div>

      {/* 목록 + 상세 */}
      <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
      {/* 좌측 목록 */}
      <div className={`sm:w-56 sm:flex-shrink-0 flex-col ${selected ? 'hidden sm:flex' : 'flex w-full'}`}>
        <div className="flex-1 overflow-y-auto flex flex-col gap-1">
          {filtered.length === 0 && (
            <div className="text-center py-10 text-xs text-gray-600">
              {search || filterCategory || filterStatus ? '검색 결과 없음' : '상품을 추가하세요'}
            </div>
          )}
          {filtered.map(p => (
            <button key={p.id} onClick={() => setSelected(p)}
              className={`w-full text-left px-3 py-2.5 rounded-xl transition-all ${
                selected?.id === p.id ? 'bg-brand/20 border border-brand/30' : 'hover:bg-gray-800 border border-transparent'
              }`}>
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <p className="text-sm font-medium text-white truncate flex-1">{p.name}</p>
                <StatusBadge status={p.status} />
              </div>
              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                {p.partNumber && <span>{p.partNumber}</span>}
                {p.category && <span>{p.category.name}</span>}
                {p.consumerPrice && <span>{p.consumerPrice.toLocaleString()}원</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 우측 상세 */}
      <div className={`flex-1 min-w-0 overflow-y-auto ${!selected ? 'hidden sm:flex sm:items-center sm:justify-center' : 'block'}`}>
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-600">
            <Package size={40} className="mb-3 opacity-30" />
            <p className="text-sm">상품을 선택하거나 추가하세요</p>
          </div>
        ) : (
          <div>
            <button className="sm:hidden flex items-center gap-1.5 text-xs text-gray-400 hover:text-white mb-3"
              onClick={() => setSelected(null)}>
              <ArrowLeft size={14} /> 목록으로
            </button>
            <ProductDetail
              key={selected.id}
              product={selected}
              categories={categories}
              onEdit={p => setModal({ product: p })}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
            />
          </div>
        )}
      </div>

      </div>

      {modal && (
        <ProductModal
          product={modal.product}
          categories={categories}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
      {catModal && (
        <CategoryModal
          categories={categories}
          onClose={() => setCatModal(false)}
          onRefresh={async () => {
            const res = await api.get('/products/categories');
            setCategories(res.data);
          }}
        />
      )}
    </div>
  );
}
